// Medical Scores Database - Complete Collection
// Organized by medical specialty

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Eye,
  Ear,
  Scissors,
  Scale,
  Clock,
  AlertTriangle,
  FileText,
  Calculator,
  Beaker,
  Microscope,
  Sparkles,
  HeartPulse,
  User,
  Users,
  Zap
} from 'lucide-react';

// ==================== ANESTHÉSIE ====================
export const ANESTHESIA_SCORES = {
  aldrete: {
    name: 'Score d\'Aldrete',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Sortie salle de réveil',
    color: 'emerald',
    fields: [
      { id: 'activity', label: 'Activité motrice', type: 'select', options: [
        { value: 2, label: '2 - Bouge les 4 membres volontairement ou sur ordre' },
        { value: 1, label: '1 - Bouge 2 membres volontairement ou sur ordre' },
        { value: 0, label: '0 - Incapable de bouger les membres' }
      ]},
      { id: 'respiration', label: 'Respiration', type: 'select', options: [
        { value: 2, label: '2 - Respire profondément et tousse librement' },
        { value: 1, label: '1 - Dyspnée ou respiration limitée' },
        { value: 0, label: '0 - Apnée' }
      ]},
      { id: 'circulation', label: 'Circulation', type: 'select', options: [
        { value: 2, label: '2 - PA ± 20% de la valeur pré-anesthésique' },
        { value: 1, label: '1 - PA ± 20-50% de la valeur pré-anesthésique' },
        { value: 0, label: '0 - PA ± 50% de la valeur pré-anesthésique' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 2, label: '2 - Complètement réveillé' },
        { value: 1, label: '1 - Réveillé à l\'appel' },
        { value: 0, label: '0 - Ne répond pas' }
      ]},
      { id: 'spo2', label: 'SpO₂', type: 'select', options: [
        { value: 2, label: '2 - SpO₂ > 92% en air ambiant' },
        { value: 1, label: '1 - SpO₂ > 90% avec O₂' },
        { value: 0, label: '0 - SpO₂ < 90% avec O₂' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score >= 9) return { level: 'success', text: 'Sortie SSPI autorisée', detail: 'Patient apte à quitter la salle de réveil' };
      if (score >= 7) return { level: 'warning', text: 'Surveillance prolongée', detail: 'Réévaluer dans 15-30 minutes' };
      return { level: 'danger', text: 'Maintien en SSPI', detail: 'Surveillance continue nécessaire' };
    },
    formula: 'Aldrete = Activité + Respiration + Circulation + Conscience + SpO₂ (≥9 pour sortie)'
  },

  apfel: {
    name: 'Score d\'Apfel',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Nausées-vomissements post-opératoires (NVPO)',
    color: 'emerald',
    fields: [
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 1 },
      { id: 'nonSmoker', label: 'Non fumeur', type: 'checkbox', points: 1 },
      { id: 'historyNVPO', label: 'ATCD NVPO ou mal des transports', type: 'checkbox', points: 1 },
      { id: 'opioids', label: 'Morphiniques post-opératoires prévus', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.female) total += 1;
      if (values.nonSmoker) total += 1;
      if (values.historyNVPO) total += 1;
      if (values.opioids) total += 1;
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      const risks = ['10%', '21%', '39%', '61%', '79%'];
      if (score === 0) return { level: 'success', text: 'Risque faible (10%)', detail: 'Pas de prophylaxie systématique' };
      if (score === 1) return { level: 'success', text: 'Risque faible (21%)', detail: 'Prophylaxie selon contexte' };
      if (score === 2) return { level: 'warning', text: 'Risque modéré (39%)', detail: 'Prophylaxie recommandée (1 antiémétique)' };
      if (score === 3) return { level: 'warning', text: 'Risque élevé (61%)', detail: 'Prophylaxie multimodale (2 antiémétiques)' };
      return { level: 'danger', text: 'Risque très élevé (79%)', detail: 'Prophylaxie multimodale + TIVA' };
    },
    formula: 'Apfel = Femme + Non-fumeur + ATCD NVPO + Morphiniques postop'
  },

  asa: {
    name: 'Classification ASA',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'État général avant anesthésie',
    color: 'emerald',
    fields: [
      { id: 'class', label: 'Classification ASA', type: 'select', options: [
        { value: 1, label: 'ASA 1 - Patient en bonne santé' },
        { value: 2, label: 'ASA 2 - Maladie systémique légère' },
        { value: 3, label: 'ASA 3 - Maladie systémique sévère' },
        { value: 4, label: 'ASA 4 - Maladie systémique mettant en jeu le pronostic vital' },
        { value: 5, label: 'ASA 5 - Patient moribond' },
        { value: 6, label: 'ASA 6 - Patient en mort cérébrale (donneur)' }
      ]},
      { id: 'emergency', label: 'Chirurgie en urgence', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      let score = values.class || 1;
      let suffix = values.emergency ? 'U' : '';
      return { score: score, max: 6, suffix: suffix };
    },
    interpret: (score) => {
      const mortalities = ['0.1%', '0.2%', '1.8%', '7.8%', '9.4%', '-'];
      if (score === 1) return { level: 'success', text: 'ASA 1 - Bonne santé', detail: `Mortalité périopératoire ~${mortalities[0]}` };
      if (score === 2) return { level: 'success', text: 'ASA 2 - Maladie légère', detail: `Mortalité ~${mortalities[1]}. Ex: tabagisme, obésité modérée, diabète équilibré` };
      if (score === 3) return { level: 'warning', text: 'ASA 3 - Maladie sévère', detail: `Mortalité ~${mortalities[2]}. Ex: diabète mal équilibré, BPCO, obésité morbide` };
      if (score === 4) return { level: 'danger', text: 'ASA 4 - Pronostic vital engagé', detail: `Mortalité ~${mortalities[3]}. Ex: IDM récent, AVC, sepsis` };
      if (score === 5) return { level: 'danger', text: 'ASA 5 - Moribond', detail: `Mortalité ~${mortalities[4]}. Survie improbable sans chirurgie` };
      return { level: 'danger', text: 'ASA 6 - Mort cérébrale', detail: 'Patient donneur d\'organes' };
    },
    formula: 'ASA I à VI, ajouter "U" si urgence'
  },

  mallampati: {
    name: 'Score de Mallampati',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Prédiction intubation difficile',
    color: 'emerald',
    fields: [
      { id: 'class', label: 'Classification Mallampati', type: 'select', options: [
        { value: 1, label: 'Classe I - Palais mou, luette, piliers visibles' },
        { value: 2, label: 'Classe II - Palais mou, luette visibles' },
        { value: 3, label: 'Classe III - Palais mou, base de luette visibles' },
        { value: 4, label: 'Classe IV - Palais dur uniquement visible' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'Classe I', detail: 'Intubation généralement facile' };
      if (score === 2) return { level: 'success', text: 'Classe II', detail: 'Intubation probablement facile' };
      if (score === 3) return { level: 'warning', text: 'Classe III', detail: 'Intubation potentiellement difficile - Préparer alternatives' };
      return { level: 'danger', text: 'Classe IV', detail: 'Intubation difficile probable - Prévoir fibroscope/vidéolaryngoscope' };
    },
    formula: 'Mallampati: évaluation bouche ouverte, langue tirée, sans phonation'
  },

  cormack: {
    name: 'Cormack et Lehane',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Difficulté intubation selon vision glotte',
    color: 'emerald',
    fields: [
      { id: 'grade', label: 'Grade de Cormack-Lehane', type: 'select', options: [
        { value: 1, label: 'Grade 1 - Glotte entièrement visible' },
        { value: 2, label: 'Grade 2 - Glotte partiellement visible' },
        { value: 3, label: 'Grade 3 - Épiglotte seule visible' },
        { value: 4, label: 'Grade 4 - Épiglotte non visible' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.grade || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'Grade 1', detail: 'Intubation facile' };
      if (score === 2) return { level: 'success', text: 'Grade 2', detail: 'Intubation généralement possible, peut nécessiter mandrin' };
      if (score === 3) return { level: 'warning', text: 'Grade 3', detail: 'Intubation difficile - Mandrin, vidéolaryngoscope recommandé' };
      return { level: 'danger', text: 'Grade 4', detail: 'Intubation très difficile - Fibroscope, dispositifs supraglottiques' };
    },
    formula: 'Cormack-Lehane: évaluation lors de la laryngoscopie directe'
  },

  macocha: {
    name: 'Score MACOCHA',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Prédiction intubation difficile en réanimation',
    color: 'emerald',
    fields: [
      { id: 'mallampati34', label: 'Mallampati III ou IV', type: 'checkbox', points: 5 },
      { id: 'apnea', label: 'Syndrome d\'apnée du sommeil', type: 'checkbox', points: 2 },
      { id: 'cervical', label: 'Mobilité rachis cervical réduite', type: 'checkbox', points: 1 },
      { id: 'mouth', label: 'Ouverture bouche < 3 cm', type: 'checkbox', points: 1 },
      { id: 'coma', label: 'Coma (Glasgow ≤ 8)', type: 'checkbox', points: 1 },
      { id: 'hypoxia', label: 'Hypoxémie sévère (SpO₂ < 80%)', type: 'checkbox', points: 1 },
      { id: 'nonAnesthesiologist', label: 'Opérateur non anesthésiste', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.mallampati34) total += 5;
      if (values.apnea) total += 2;
      if (values.cervical) total += 1;
      if (values.mouth) total += 1;
      if (values.coma) total += 1;
      if (values.hypoxia) total += 1;
      if (values.nonAnesthesiologist) total += 1;
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: 'Risque faible', detail: 'Intubation difficile peu probable (~3%)' };
      if (score <= 4) return { level: 'warning', text: 'Risque modéré', detail: 'Risque intubation difficile ~10-15%' };
      return { level: 'danger', text: 'Risque élevé', detail: 'Risque intubation difficile >35% - Préparer alternatives' };
    },
    formula: 'MACOCHA = Mallampati(5) + Apnée sommeil(2) + Cervical(1) + Ouverture bouche(1) + Coma(1) + Hypoxie(1) + Non-anesthésiste(1)'
  },

  ramsay: {
    name: 'Échelle de Ramsay',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Niveau de sédation',
    color: 'emerald',
    fields: [
      { id: 'level', label: 'Niveau de sédation', type: 'select', options: [
        { value: 1, label: '1 - Patient anxieux, agité' },
        { value: 2, label: '2 - Patient coopérant, orienté, calme' },
        { value: 3, label: '3 - Répond aux ordres simples' },
        { value: 4, label: '4 - Endormi, réponse vive à stimulation glabelle/bruit' },
        { value: 5, label: '5 - Endormi, réponse lente à stimulation' },
        { value: 6, label: '6 - Pas de réponse aux stimulations' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.level || 1, max: 6 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'danger', text: 'Ramsay 1 - Agitation', detail: 'Sédation insuffisante, augmenter les doses' };
      if (score === 2) return { level: 'success', text: 'Ramsay 2 - Coopérant', detail: 'État idéal pour patient éveillé' };
      if (score === 3) return { level: 'success', text: 'Ramsay 3 - Somnolent', detail: 'Sédation légère acceptable' };
      if (score === 4) return { level: 'warning', text: 'Ramsay 4 - Endormi', detail: 'Sédation modérée, objectif habituel en réanimation' };
      if (score === 5) return { level: 'warning', text: 'Ramsay 5 - Sédation profonde', detail: 'Réponse lente, surveiller la profondeur' };
      return { level: 'danger', text: 'Ramsay 6 - Coma', detail: 'Sédation trop profonde, diminuer les doses' };
    },
    formula: 'Ramsay 1-6: objectif habituel = 3-4 en réanimation'
  },

  rass: {
    name: 'Échelle RASS',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Richmond Agitation-Sedation Scale',
    color: 'emerald',
    fields: [
      { id: 'level', label: 'Niveau RASS', type: 'select', options: [
        { value: 4, label: '+4 - Combatif, violent, danger immédiat' },
        { value: 3, label: '+3 - Très agité, arrache sondes/cathéters' },
        { value: 2, label: '+2 - Agité, mouvements fréquents non coordonnés' },
        { value: 1, label: '+1 - Anxieux, inquiet mais mouvements non agressifs' },
        { value: 0, label: '0 - Éveillé et calme' },
        { value: -1, label: '-1 - Somnolent, yeux ouverts >10s à la voix' },
        { value: -2, label: '-2 - Sédation légère, yeux ouverts <10s à la voix' },
        { value: -3, label: '-3 - Sédation modérée, mouvement à la voix sans contact visuel' },
        { value: -4, label: '-4 - Sédation profonde, mouvement à stimulation physique' },
        { value: -5, label: '-5 - Non réveillable, pas de réponse' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.level || 0, max: '+4', min: '-5' };
    },
    interpret: (score) => {
      if (score >= 3) return { level: 'danger', text: `RASS +${score} - Agitation sévère`, detail: 'Danger pour patient/soignant, sédation urgente' };
      if (score >= 1) return { level: 'warning', text: `RASS +${score} - Agitation`, detail: 'Rechercher cause (douleur, sevrage, délirium)' };
      if (score === 0) return { level: 'success', text: 'RASS 0 - Éveillé calme', detail: 'État optimal' };
      if (score >= -2) return { level: 'success', text: `RASS ${score} - Sédation légère`, detail: 'Objectif habituel en réanimation' };
      if (score >= -4) return { level: 'warning', text: `RASS ${score} - Sédation profonde`, detail: 'Surveiller, diminuer sédation si possible' };
      return { level: 'danger', text: 'RASS -5 - Non réveillable', detail: 'Sédation trop profonde ou coma' };
    },
    formula: 'RASS: -5 (coma) à +4 (combatif). Objectif: 0 à -2'
  },

  lee: {
    name: 'Score de Lee (RCRI)',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Risque cardio-vasculaire péri-opératoire',
    color: 'emerald',
    fields: [
      { id: 'highRiskSurgery', label: 'Chirurgie à haut risque (intra-péritonéale, thoracique, vasculaire)', type: 'checkbox', points: 1 },
      { id: 'coronary', label: 'Cardiopathie ischémique', type: 'checkbox', points: 1 },
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'ATCD AVC ou AIT', type: 'checkbox', points: 1 },
      { id: 'insulin', label: 'Diabète sous insuline', type: 'checkbox', points: 1 },
      { id: 'creatinine', label: 'Créatinine > 177 µmol/L (2 mg/dL)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      const risks = ['0.4%', '0.9%', '6.6%', '11%'];
      if (score === 0) return { level: 'success', text: 'Classe I - Risque très faible', detail: `Risque complication cardiaque majeure: ${risks[0]}` };
      if (score === 1) return { level: 'success', text: 'Classe II - Risque faible', detail: `Risque complication cardiaque majeure: ${risks[1]}` };
      if (score === 2) return { level: 'warning', text: 'Classe III - Risque modéré', detail: `Risque complication cardiaque majeure: ${risks[2]}` };
      return { level: 'danger', text: 'Classe IV - Risque élevé', detail: `Risque complication cardiaque majeure: ≥${risks[3]}` };
    },
    formula: 'Lee/RCRI = Somme des 6 facteurs de risque (0-6)'
  },

  ariscat: {
    name: 'Score ARISCAT',
    category: 'Anesthésie',
    icon: Syringe,
    description: 'Complications respiratoires post-opératoires',
    color: 'emerald',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '≤ 50 ans (0 pts)' },
        { value: 3, label: '51-80 ans (3 pts)' },
        { value: 16, label: '> 80 ans (16 pts)' }
      ]},
      { id: 'spo2', label: 'SpO₂ préopératoire', type: 'select', options: [
        { value: 0, label: '≥ 96% (0 pts)' },
        { value: 8, label: '91-95% (8 pts)' },
        { value: 24, label: '≤ 90% (24 pts)' }
      ]},
      { id: 'respiratory', label: 'Infection respiratoire < 1 mois', type: 'checkbox', points: 17 },
      { id: 'anemia', label: 'Anémie préopératoire (Hb ≤ 10 g/dL)', type: 'checkbox', points: 11 },
      { id: 'surgicalSite', label: 'Site chirurgical', type: 'select', options: [
        { value: 0, label: 'Périphérique (0 pts)' },
        { value: 15, label: 'Abdomen supérieur (15 pts)' },
        { value: 24, label: 'Intrathoracique (24 pts)' }
      ]},
      { id: 'duration', label: 'Durée chirurgie', type: 'select', options: [
        { value: 0, label: '< 2h (0 pts)' },
        { value: 16, label: '2-3h (16 pts)' },
        { value: 23, label: '> 3h (23 pts)' }
      ]},
      { id: 'emergency', label: 'Chirurgie en urgence', type: 'checkbox', points: 8 }
    ],
    calculate: (values) => {
      let total = (values.age || 0) + (values.spo2 || 0) + (values.surgicalSite || 0) + (values.duration || 0);
      if (values.respiratory) total += 17;
      if (values.anemia) total += 11;
      if (values.emergency) total += 8;
      return { score: total, max: 123 };
    },
    interpret: (score) => {
      if (score < 26) return { level: 'success', text: 'Risque faible (1.6%)', detail: 'Complications respiratoires post-op peu probables' };
      if (score < 45) return { level: 'warning', text: 'Risque intermédiaire (13.3%)', detail: 'Surveillance rapprochée recommandée' };
      return { level: 'danger', text: 'Risque élevé (42.1%)', detail: 'Prévention active: kinésithérapie, VNI postop, USI' };
    },
    formula: 'ARISCAT = Âge + SpO₂ + Infection respi + Anémie + Site + Durée + Urgence'
  }
};

// ==================== BRÛLÉS ====================
export const BURNS_SCORES = {
  wallace: {
    name: 'Règle des 9 de Wallace',
    category: 'Brûlés',
    icon: Flame,
    description: 'Calcul surface brûlée adulte',
    color: 'orange',
    fields: [
      { id: 'head', label: 'Tête et cou', type: 'select', options: [
        { value: 0, label: '0% - Non brûlé' },
        { value: 4.5, label: '4.5% - Face antérieure' },
        { value: 4.5, label: '4.5% - Face postérieure' },
        { value: 9, label: '9% - Totalité' }
      ]},
      { id: 'trunkAnt', label: 'Tronc antérieur', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 9, label: '9%' }, { value: 18, label: '18%' }
      ]},
      { id: 'trunkPost', label: 'Tronc postérieur', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 9, label: '9%' }, { value: 18, label: '18%' }
      ]},
      { id: 'armR', label: 'Bras droit (totalité)', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 4.5, label: '4.5%' }, { value: 9, label: '9%' }
      ]},
      { id: 'armL', label: 'Bras gauche (totalité)', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 4.5, label: '4.5%' }, { value: 9, label: '9%' }
      ]},
      { id: 'legR', label: 'Jambe droite (totalité)', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 9, label: '9%' }, { value: 18, label: '18%' }
      ]},
      { id: 'legL', label: 'Jambe gauche (totalité)', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 9, label: '9%' }, { value: 18, label: '18%' }
      ]},
      { id: 'perineum', label: 'Périnée', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 1, label: '1%' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (parseFloat(b) || 0), 0);
      return { score: total, max: 100, unit: '%' };
    },
    interpret: (score) => {
      if (score < 10) return { level: 'success', text: `SCB ${score}%`, detail: 'Brûlure mineure - Traitement ambulatoire possible' };
      if (score < 20) return { level: 'warning', text: `SCB ${score}%`, detail: 'Brûlure modérée - Hospitalisation, remplissage selon formule' };
      if (score < 50) return { level: 'danger', text: `SCB ${score}%`, detail: 'Brûlure grave - Centre des brûlés, remplissage massif' };
      return { level: 'danger', text: `SCB ${score}%`, detail: 'Brûlure très grave - Pronostic vital engagé' };
    },
    formula: 'Tête 9% | Tronc ant/post 18% chaque | Bras 9% chaque | Jambe 18% chaque | Périnée 1%'
  },

  parkland: {
    name: 'Formule de Parkland',
    category: 'Brûlés',
    icon: Flame,
    description: 'Remplissage brûlés graves adulte',
    color: 'orange',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 200 },
      { id: 'scb', label: 'Surface corporelle brûlée (%)', type: 'number', min: 1, max: 100 }
    ],
    calculate: (values) => {
      const weight = values.weight || 70;
      const scb = values.scb || 20;
      const total24h = 4 * weight * scb;
      const first8h = total24h / 2;
      const next16h = total24h / 2;
      return { 
        score: total24h, 
        max: 'mL/24h',
        details: { first8h, next16h, hourlyFirst8h: Math.round(first8h / 8), hourlyNext16h: Math.round(next16h / 16) }
      };
    },
    interpret: (score, result) => {
      const d = result?.details || {};
      return { 
        level: 'warning', 
        text: `Volume total: ${score} mL/24h`, 
        detail: `H0-H8: ${d.first8h} mL (${d.hourlyFirst8h} mL/h) | H8-H24: ${d.next16h} mL (${d.hourlyNext16h} mL/h). Utiliser Ringer Lactate. Adapter selon diurèse (0.5-1 mL/kg/h)`
      };
    },
    formula: 'Parkland = 4 mL × Poids(kg) × SCB(%). 50% dans les 8 premières heures'
  },

  absi: {
    name: 'Score ABSI',
    category: 'Brûlés',
    icon: Flame,
    description: 'Probabilité de survie patient brûlé',
    color: 'orange',
    fields: [
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 0, label: 'Femme (0 pts)' },
        { value: 1, label: 'Homme (1 pt)' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 1, label: '0-20 ans (1 pt)' },
        { value: 2, label: '21-40 ans (2 pts)' },
        { value: 3, label: '41-60 ans (3 pts)' },
        { value: 4, label: '61-80 ans (4 pts)' },
        { value: 5, label: '> 80 ans (5 pts)' }
      ]},
      { id: 'inhalation', label: 'Brûlure par inhalation', type: 'checkbox', points: 1 },
      { id: 'fullThickness', label: 'Brûlure de pleine épaisseur (3ème degré)', type: 'checkbox', points: 1 },
      { id: 'scb', label: 'Surface corporelle brûlée', type: 'select', options: [
        { value: 1, label: '1-10% (1 pt)' },
        { value: 2, label: '11-20% (2 pts)' },
        { value: 3, label: '21-30% (3 pts)' },
        { value: 4, label: '31-40% (4 pts)' },
        { value: 5, label: '41-50% (5 pts)' },
        { value: 6, label: '51-60% (6 pts)' },
        { value: 7, label: '61-70% (7 pts)' },
        { value: 8, label: '71-80% (8 pts)' },
        { value: 9, label: '81-90% (9 pts)' },
        { value: 10, label: '91-100% (10 pts)' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.sex || 0) + (values.age || 1) + (values.scb || 1);
      if (values.inhalation) total += 1;
      if (values.fullThickness) total += 1;
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: 'Survie > 99%', detail: 'Très bon pronostic' };
      if (score <= 6) return { level: 'success', text: 'Survie ~98%', detail: 'Bon pronostic' };
      if (score <= 8) return { level: 'warning', text: 'Survie ~80-90%', detail: 'Pronostic réservé' };
      if (score <= 10) return { level: 'warning', text: 'Survie ~50-70%', detail: 'Pronostic sombre' };
      if (score <= 12) return { level: 'danger', text: 'Survie ~20-40%', detail: 'Pronostic très sombre' };
      return { level: 'danger', text: 'Survie < 10%', detail: 'Soins palliatifs à discuter' };
    },
    formula: 'ABSI = Sexe + Âge + SCB + Inhalation + Pleine épaisseur'
  }
};

// ==================== CARDIOLOGIE ÉTENDUE ====================
export const CARDIOLOGY_EXTENDED_SCORES = {
  heart: {
    name: 'Score HEART',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Hospitalisation douleur thoracique',
    color: 'red',
    fields: [
      { id: 'history', label: 'Anamnèse', type: 'select', options: [
        { value: 0, label: '0 - Peu suspecte' },
        { value: 1, label: '1 - Modérément suspecte' },
        { value: 2, label: '2 - Très suspecte' }
      ]},
      { id: 'ecg', label: 'ECG', type: 'select', options: [
        { value: 0, label: '0 - Normal' },
        { value: 1, label: '1 - Anomalie de repolarisation non spécifique' },
        { value: 2, label: '2 - Déviation ST significative' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '0 - < 45 ans' },
        { value: 1, label: '1 - 45-64 ans' },
        { value: 2, label: '2 - ≥ 65 ans' }
      ]},
      { id: 'riskFactors', label: 'Facteurs de risque', type: 'select', options: [
        { value: 0, label: '0 - Aucun' },
        { value: 1, label: '1 - 1-2 facteurs (HTA, diabète, tabac, dyslipidémie, obésité, hérédité)' },
        { value: 2, label: '2 - ≥ 3 facteurs ou ATCD coronarien' }
      ]},
      { id: 'troponin', label: 'Troponine', type: 'select', options: [
        { value: 0, label: '0 - Normale' },
        { value: 1, label: '1 - 1-3× normale' },
        { value: 2, label: '2 - > 3× normale' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: 'Risque faible (1.6%)', detail: 'Sortie possible si troponine de contrôle négative' };
      if (score <= 6) return { level: 'warning', text: 'Risque intermédiaire (12%)', detail: 'Hospitalisation pour surveillance et bilan' };
      return { level: 'danger', text: 'Risque élevé (50%)', detail: 'USIC, stratégie invasive précoce' };
    },
    formula: 'HEART = History + ECG + Age + Risk factors + Troponin'
  },

  killip: {
    name: 'Classification de Killip',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Gravité insuffisance cardiaque sur IDM',
    color: 'red',
    fields: [
      { id: 'class', label: 'Classe Killip', type: 'select', options: [
        { value: 1, label: 'Classe I - Pas d\'insuffisance cardiaque' },
        { value: 2, label: 'Classe II - Râles crépitants, B3, turgescence jugulaire' },
        { value: 3, label: 'Classe III - OAP franc' },
        { value: 4, label: 'Classe IV - Choc cardiogénique' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      const mortalities = ['6%', '17%', '38%', '81%'];
      if (score === 1) return { level: 'success', text: 'Killip I', detail: `Mortalité hospitalière ~${mortalities[0]}. Pas d'IC clinique` };
      if (score === 2) return { level: 'warning', text: 'Killip II', detail: `Mortalité ~${mortalities[1]}. IC modérée, traitement diurétique` };
      if (score === 3) return { level: 'danger', text: 'Killip III', detail: `Mortalité ~${mortalities[2]}. OAP, USIC, VNI/IOT si besoin` };
      return { level: 'danger', text: 'Killip IV', detail: `Mortalité ~${mortalities[3]}. Choc cardiogénique, assistance circulatoire` };
    },
    formula: 'Killip I-IV: classification de l\'insuffisance cardiaque aiguë sur IDM'
  },

  nyha: {
    name: 'Classification NYHA',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Gravité insuffisance cardiaque chronique',
    color: 'red',
    fields: [
      { id: 'class', label: 'Classe NYHA', type: 'select', options: [
        { value: 1, label: 'Classe I - Pas de limitation, activité physique ordinaire asymptomatique' },
        { value: 2, label: 'Classe II - Limitation légère, symptômes pour efforts importants' },
        { value: 3, label: 'Classe III - Limitation marquée, symptômes pour efforts minimes' },
        { value: 4, label: 'Classe IV - Symptômes au repos' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'NYHA I', detail: 'IC asymptomatique. Traitement de fond, surveillance' };
      if (score === 2) return { level: 'success', text: 'NYHA II', detail: 'IC légère. Optimiser traitement médicamenteux' };
      if (score === 3) return { level: 'warning', text: 'NYHA III', detail: 'IC modérée-sévère. Traitement intensif, discuter resynchronisation/DAI' };
      return { level: 'danger', text: 'NYHA IV', detail: 'IC sévère. Hospitalisation, inotropes, assistance/greffe à discuter' };
    },
    formula: 'NYHA I-IV: évaluation fonctionnelle de l\'insuffisance cardiaque'
  },

  grace: {
    name: 'Score GRACE',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Mortalité SCA non ST+',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge', type: 'number', min: 18, max: 120 },
      { id: 'hr', label: 'Fréquence cardiaque', type: 'number', min: 30, max: 250 },
      { id: 'sbp', label: 'PAS (mmHg)', type: 'number', min: 50, max: 250 },
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 20, max: 800 },
      { id: 'killip', label: 'Classe Killip', type: 'select', options: [
        { value: 1, label: 'Killip I' },
        { value: 2, label: 'Killip II' },
        { value: 3, label: 'Killip III' },
        { value: 4, label: 'Killip IV' }
      ]},
      { id: 'cardiacArrest', label: 'Arrêt cardiaque à l\'admission', type: 'checkbox', points: 39 },
      { id: 'stDeviation', label: 'Déviation ST', type: 'checkbox', points: 28 },
      { id: 'elevatedMarkers', label: 'Marqueurs cardiaques élevés', type: 'checkbox', points: 14 }
    ],
    calculate: (values) => {
      let total = 0;
      // Age points (simplified)
      const age = values.age || 60;
      if (age < 30) total += 0;
      else if (age < 40) total += 8;
      else if (age < 50) total += 25;
      else if (age < 60) total += 41;
      else if (age < 70) total += 58;
      else if (age < 80) total += 75;
      else if (age < 90) total += 91;
      else total += 100;
      
      // HR points
      const hr = values.hr || 80;
      if (hr < 50) total += 0;
      else if (hr < 70) total += 3;
      else if (hr < 90) total += 9;
      else if (hr < 110) total += 15;
      else if (hr < 150) total += 24;
      else if (hr < 200) total += 38;
      else total += 46;
      
      // SBP points
      const sbp = values.sbp || 120;
      if (sbp < 80) total += 58;
      else if (sbp < 100) total += 53;
      else if (sbp < 120) total += 43;
      else if (sbp < 140) total += 34;
      else if (sbp < 160) total += 24;
      else if (sbp < 200) total += 10;
      else total += 0;
      
      // Creatinine points
      const creat = values.creatinine || 100;
      if (creat < 35) total += 1;
      else if (creat < 71) total += 4;
      else if (creat < 106) total += 7;
      else if (creat < 141) total += 10;
      else if (creat < 177) total += 13;
      else if (creat < 353) total += 21;
      else total += 28;
      
      // Killip points
      const killip = values.killip || 1;
      total += (killip - 1) * 20;
      
      // Other factors
      if (values.cardiacArrest) total += 39;
      if (values.stDeviation) total += 28;
      if (values.elevatedMarkers) total += 14;
      
      return { score: total, max: 372 };
    },
    interpret: (score) => {
      if (score <= 108) return { level: 'success', text: 'Risque faible', detail: 'Mortalité hospitalière < 1%. Stratégie conservative possible' };
      if (score <= 140) return { level: 'warning', text: 'Risque intermédiaire', detail: 'Mortalité 1-3%. Coronarographie < 72h' };
      return { level: 'danger', text: 'Risque élevé', detail: 'Mortalité > 3%. Stratégie invasive précoce < 24h' };
    },
    formula: 'GRACE = Âge + FC + PAS + Créat + Killip + ACR + ST + Troponine'
  },

  timiNstemi: {
    name: 'TIMI SCA non ST+',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Mortalité et événements SCA non ST+',
    color: 'red',
    fields: [
      { id: 'age65', label: 'Âge ≥ 65 ans', type: 'checkbox', points: 1 },
      { id: 'risk3', label: '≥ 3 facteurs de risque CV', type: 'checkbox', points: 1 },
      { id: 'knownCAD', label: 'Coronaropathie connue (sténose ≥ 50%)', type: 'checkbox', points: 1 },
      { id: 'aspirin', label: 'Aspirine dans les 7 jours', type: 'checkbox', points: 1 },
      { id: 'angina2', label: '≥ 2 épisodes angineux en 24h', type: 'checkbox', points: 1 },
      { id: 'stDeviation', label: 'Déviation ST ≥ 0.5 mm', type: 'checkbox', points: 1 },
      { id: 'markers', label: 'Élévation marqueurs cardiaques', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 7 };
    },
    interpret: (score) => {
      const risks = ['4.7%', '8.3%', '13.2%', '19.9%', '26.2%', '40.9%'];
      if (score <= 2) return { level: 'success', text: `Risque faible (${risks[Math.min(score, 5)]})`, detail: 'Stratégie conservative ou invasive selon clinique' };
      if (score <= 4) return { level: 'warning', text: `Risque intermédiaire (${risks[Math.min(score, 5)]})`, detail: 'Stratégie invasive recommandée' };
      return { level: 'danger', text: `Risque élevé (${risks[Math.min(score, 5)]})`, detail: 'Stratégie invasive précoce < 24h' };
    },
    formula: 'TIMI = 7 critères (1 point chacun). Risque = décès/IDM/revascularisation urgente à 14j'
  },

  qtc: {
    name: 'QT corrigé',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Calcul du QTc',
    color: 'red',
    fields: [
      { id: 'qt', label: 'QT mesuré (ms)', type: 'number', min: 200, max: 700 },
      { id: 'rr', label: 'Intervalle RR (ms) ou FC', type: 'number', min: 30, max: 2000 },
      { id: 'inputType', label: 'Type d\'entrée', type: 'select', options: [
        { value: 'rr', label: 'Intervalle RR (ms)' },
        { value: 'fc', label: 'Fréquence cardiaque (bpm)' }
      ]}
    ],
    calculate: (values) => {
      const qt = values.qt || 400;
      let rr = values.rr || 1000;
      if (values.inputType === 'fc') {
        rr = 60000 / rr; // Convert FC to RR
      }
      // Bazett formula
      const qtcBazett = Math.round(qt / Math.sqrt(rr / 1000));
      // Fridericia formula
      const qtcFridericia = Math.round(qt / Math.pow(rr / 1000, 1/3));
      return { score: qtcBazett, max: 'ms', details: { bazett: qtcBazett, fridericia: qtcFridericia } };
    },
    interpret: (score, result) => {
      const d = result?.details || {};
      if (score < 440) return { level: 'success', text: `QTc normal: ${score} ms`, detail: `Bazett: ${d.bazett} ms | Fridericia: ${d.fridericia} ms` };
      if (score < 480) return { level: 'warning', text: `QTc limite: ${score} ms`, detail: `Surveillance, éviter médicaments allongeant QT. Fridericia: ${d.fridericia} ms` };
      if (score < 500) return { level: 'danger', text: `QTc allongé: ${score} ms`, detail: `Risque de torsades de pointes. Arrêter médicaments incriminés` };
      return { level: 'danger', text: `QTc très allongé: ${score} ms`, detail: `Risque majeur torsades de pointes. Surveillance scope, Mg IV` };
    },
    formula: 'QTc Bazett = QT / √RR | QTc Fridericia = QT / ∛RR'
  },

  pam: {
    name: 'Pression Artérielle Moyenne',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Calcul de la PAM',
    color: 'red',
    fields: [
      { id: 'pas', label: 'PAS (mmHg)', type: 'number', min: 50, max: 300 },
      { id: 'pad', label: 'PAD (mmHg)', type: 'number', min: 20, max: 200 }
    ],
    calculate: (values) => {
      const pas = values.pas || 120;
      const pad = values.pad || 80;
      const pam = Math.round((pas + 2 * pad) / 3);
      const pp = pas - pad;
      return { score: pam, max: 'mmHg', details: { pp } };
    },
    interpret: (score, result) => {
      const pp = result?.details?.pp || 40;
      if (score < 65) return { level: 'danger', text: `PAM basse: ${score} mmHg`, detail: `Risque hypoperfusion organes. PP: ${pp} mmHg. Objectif PAM ≥ 65 mmHg` };
      if (score < 70) return { level: 'warning', text: `PAM limite: ${score} mmHg`, detail: `PP: ${pp} mmHg. Surveillance, optimiser volémie` };
      if (score <= 105) return { level: 'success', text: `PAM normale: ${score} mmHg`, detail: `PP: ${pp} mmHg` };
      return { level: 'warning', text: `PAM élevée: ${score} mmHg`, detail: `PP: ${pp} mmHg. Rechercher HTA mal contrôlée` };
    },
    formula: 'PAM = (PAS + 2×PAD) / 3 | PP = PAS - PAD'
  }
};

// ==================== NÉPHROLOGIE ====================
export const NEPHROLOGY_SCORES = {
  cockcroft: {
    name: 'Cockcroft-Gault',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Clairance de la créatinine',
    color: 'blue',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 120 },
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 250 },
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 20, max: 2000 },
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const weight = values.weight || 70;
      const creat = values.creatinine || 100;
      const k = values.female ? 1.04 : 1.23;
      const clcr = Math.round((140 - age) * weight * k / creat);
      return { score: clcr, max: 'mL/min' };
    },
    interpret: (score) => {
      if (score >= 90) return { level: 'success', text: `ClCr: ${score} mL/min`, detail: 'Fonction rénale normale (stade 1)' };
      if (score >= 60) return { level: 'success', text: `ClCr: ${score} mL/min`, detail: 'IRC légère (stade 2). Pas d\'adaptation posologique' };
      if (score >= 30) return { level: 'warning', text: `ClCr: ${score} mL/min`, detail: 'IRC modérée (stade 3). Adapter posologies médicaments' };
      if (score >= 15) return { level: 'danger', text: `ClCr: ${score} mL/min`, detail: 'IRC sévère (stade 4). Préparer suppléance rénale' };
      return { level: 'danger', text: `ClCr: ${score} mL/min`, detail: 'IRC terminale (stade 5). Dialyse/greffe' };
    },
    formula: 'ClCr = [(140-âge) × poids × k] / créatinine. k=1.23 (H) ou 1.04 (F)'
  },

  ckdEpi2021: {
    name: 'CKD-EPI 2021',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'DFG estimé (formule 2021 sans ethnie)',
    color: 'blue',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 120 },
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 20, max: 2000 },
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const creatMgdL = (values.creatinine || 100) / 88.4; // Convert to mg/dL
      const female = values.female;
      
      let dfg;
      if (female) {
        if (creatMgdL <= 0.7) {
          dfg = 142 * Math.pow(creatMgdL / 0.7, -0.241) * Math.pow(0.9938, age) * 1.012;
        } else {
          dfg = 142 * Math.pow(creatMgdL / 0.7, -1.2) * Math.pow(0.9938, age) * 1.012;
        }
      } else {
        if (creatMgdL <= 0.9) {
          dfg = 142 * Math.pow(creatMgdL / 0.9, -0.302) * Math.pow(0.9938, age);
        } else {
          dfg = 142 * Math.pow(creatMgdL / 0.9, -1.2) * Math.pow(0.9938, age);
        }
      }
      return { score: Math.round(dfg), max: 'mL/min/1.73m²' };
    },
    interpret: (score) => {
      if (score >= 90) return { level: 'success', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Stade G1 - Fonction rénale normale ou élevée' };
      if (score >= 60) return { level: 'success', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Stade G2 - Légèrement diminué' };
      if (score >= 45) return { level: 'warning', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Stade G3a - Modérément diminué' };
      if (score >= 30) return { level: 'warning', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Stade G3b - Modérément à sévèrement diminué' };
      if (score >= 15) return { level: 'danger', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Stade G4 - Sévèrement diminué' };
      return { level: 'danger', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Stade G5 - Insuffisance rénale terminale' };
    },
    formula: 'CKD-EPI 2021 (sans coefficient ethnique)'
  },

  schwartz: {
    name: 'Schwartz (pédiatrique)',
    category: 'Néphrologie',
    icon: Baby,
    description: 'DFG estimé pédiatrique',
    color: 'blue',
    fields: [
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 40, max: 200 },
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 10, max: 500 }
    ],
    calculate: (values) => {
      const height = values.height || 100;
      const creatMgdL = (values.creatinine || 50) / 88.4;
      // Formule de Schwartz révisée (2009) : k = 0.413
      const dfg = Math.round(0.413 * height / creatMgdL);
      return { score: dfg, max: 'mL/min/1.73m²' };
    },
    interpret: (score) => {
      if (score >= 90) return { level: 'success', text: `DFG: ${score} mL/min/1.73m²`, detail: 'Fonction rénale normale' };
      if (score >= 60) return { level: 'success', text: `DFG: ${score} mL/min/1.73m²`, detail: 'IRC légère' };
      if (score >= 30) return { level: 'warning', text: `DFG: ${score} mL/min/1.73m²`, detail: 'IRC modérée. Avis néphropédiatrique' };
      if (score >= 15) return { level: 'danger', text: `DFG: ${score} mL/min/1.73m²`, detail: 'IRC sévère. Suivi spécialisé' };
      return { level: 'danger', text: `DFG: ${score} mL/min/1.73m²`, detail: 'IRC terminale. Suppléance rénale' };
    },
    formula: 'Schwartz 2009: DFG = 0.413 × Taille(cm) / Créat(mg/dL)'
  },

  fraExcretNa: {
    name: 'Fraction excrétée du Sodium',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Distinction IRA fonctionnelle/organique',
    color: 'blue',
    fields: [
      { id: 'naU', label: 'Na urinaire (mmol/L)', type: 'number', min: 1, max: 300 },
      { id: 'naP', label: 'Na plasmatique (mmol/L)', type: 'number', min: 100, max: 180 },
      { id: 'creatU', label: 'Créatinine urinaire (µmol/L)', type: 'number', min: 500, max: 30000 },
      { id: 'creatP', label: 'Créatinine plasmatique (µmol/L)', type: 'number', min: 20, max: 2000 }
    ],
    calculate: (values) => {
      const naU = values.naU || 20;
      const naP = values.naP || 140;
      const creatU = values.creatU || 5000;
      const creatP = values.creatP || 150;
      const feNa = ((naU * creatP) / (naP * creatU)) * 100;
      return { score: Math.round(feNa * 100) / 100, max: '%' };
    },
    interpret: (score) => {
      if (score < 1) return { level: 'success', text: `FeNa: ${score}%`, detail: 'IRA fonctionnelle (pré-rénale). Remplissage vasculaire' };
      if (score < 2) return { level: 'warning', text: `FeNa: ${score}%`, detail: 'Zone grise. Contexte clinique déterminant' };
      return { level: 'danger', text: `FeNa: ${score}%`, detail: 'IRA organique (nécrose tubulaire aiguë probable)' };
    },
    formula: 'FeNa = (Na_u × Créat_p) / (Na_p × Créat_u) × 100'
  },

  osmPlasma: {
    name: 'Osmolarité plasmatique',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Calcul osmolarité',
    color: 'blue',
    fields: [
      { id: 'na', label: 'Natrémie (mmol/L)', type: 'number', min: 100, max: 180 },
      { id: 'glucose', label: 'Glycémie (mmol/L)', type: 'number', min: 1, max: 100 },
      { id: 'urea', label: 'Urée (mmol/L)', type: 'number', min: 1, max: 100 }
    ],
    calculate: (values) => {
      const na = values.na || 140;
      const glucose = values.glucose || 5;
      const urea = values.urea || 5;
      const osmCalculee = 2 * na + glucose + urea;
      const osmEfficace = 2 * na + glucose; // Osmolarité efficace (tonique)
      return { score: Math.round(osmCalculee), max: 'mOsm/L', details: { efficace: Math.round(osmEfficace) } };
    },
    interpret: (score, result) => {
      const efficace = result?.details?.efficace || score;
      if (score < 280) return { level: 'warning', text: `Osm: ${score} mOsm/L`, detail: `Hypo-osmolaire. Osm efficace: ${efficace}. Rechercher hyponatrémie` };
      if (score <= 295) return { level: 'success', text: `Osm: ${score} mOsm/L`, detail: `Normale. Osm efficace: ${efficace}` };
      if (score <= 320) return { level: 'warning', text: `Osm: ${score} mOsm/L`, detail: `Hyper-osmolaire modérée. Osm efficace: ${efficace}` };
      return { level: 'danger', text: `Osm: ${score} mOsm/L`, detail: `Hyper-osmolaire sévère. Osm efficace: ${efficace}. Risque coma hyperosmolaire` };
    },
    formula: 'Osm = 2×Na + Glucose + Urée (mOsm/L)'
  }
};

// ==================== GASTRO-ENTÉROLOGIE ====================
export const GASTRO_SCORES = {
  childPugh: {
    name: 'Score de Child-Pugh',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Sévérité cirrhose hépatique',
    color: 'amber',
    fields: [
      { id: 'encephalopathy', label: 'Encéphalopathie', type: 'select', options: [
        { value: 1, label: '1 - Absente' },
        { value: 2, label: '2 - Légère (stade I-II)' },
        { value: 3, label: '3 - Sévère (stade III-IV)' }
      ]},
      { id: 'ascites', label: 'Ascite', type: 'select', options: [
        { value: 1, label: '1 - Absente' },
        { value: 2, label: '2 - Modérée (traitement médical)' },
        { value: 3, label: '3 - Tendue (ponctions répétées)' }
      ]},
      { id: 'bilirubin', label: 'Bilirubine (µmol/L)', type: 'select', options: [
        { value: 1, label: '1 - < 35' },
        { value: 2, label: '2 - 35-50' },
        { value: 3, label: '3 - > 50' }
      ]},
      { id: 'albumin', label: 'Albumine (g/L)', type: 'select', options: [
        { value: 1, label: '1 - > 35' },
        { value: 2, label: '2 - 28-35' },
        { value: 3, label: '3 - < 28' }
      ]},
      { id: 'inr', label: 'INR / TP', type: 'select', options: [
        { value: 1, label: '1 - INR < 1.7 (TP > 50%)' },
        { value: 2, label: '2 - INR 1.7-2.3 (TP 40-50%)' },
        { value: 3, label: '3 - INR > 2.3 (TP < 40%)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 15 };
    },
    interpret: (score) => {
      if (score <= 6) return { level: 'success', text: 'Child A (5-6)', detail: 'Cirrhose compensée. Survie à 1 an: 100%' };
      if (score <= 9) return { level: 'warning', text: 'Child B (7-9)', detail: 'Cirrhose modérée. Survie à 1 an: 80%' };
      return { level: 'danger', text: 'Child C (10-15)', detail: 'Cirrhose sévère. Survie à 1 an: 45%. Discuter greffe' };
    },
    formula: 'Child-Pugh = Encéphalopathie + Ascite + Bilirubine + Albumine + INR'
  },

  meld: {
    name: 'Score MELD',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Gravité cirrhose, priorité greffe',
    color: 'amber',
    fields: [
      { id: 'bilirubin', label: 'Bilirubine (mg/dL)', type: 'number', min: 0.1, max: 50 },
      { id: 'inr', label: 'INR', type: 'number', min: 0.8, max: 10, step: 0.1 },
      { id: 'creatinine', label: 'Créatinine (mg/dL)', type: 'number', min: 0.1, max: 15, step: 0.1 },
      { id: 'dialysis', label: 'Dialyse (≥2 fois/semaine)', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      let bili = Math.max(values.bilirubin || 1, 1);
      let inr = Math.max(values.inr || 1, 1);
      let creat = Math.max(values.creatinine || 1, 1);
      if (values.dialysis) creat = 4; // Cap at 4 if on dialysis
      creat = Math.min(creat, 4);
      
      const meld = Math.round(
        10 * (0.957 * Math.log(creat) + 0.378 * Math.log(bili) + 1.12 * Math.log(inr) + 0.643)
      );
      return { score: Math.min(Math.max(meld, 6), 40), max: 40 };
    },
    interpret: (score) => {
      if (score < 10) return { level: 'success', text: `MELD: ${score}`, detail: 'Mortalité à 3 mois: < 2%' };
      if (score < 20) return { level: 'warning', text: `MELD: ${score}`, detail: 'Mortalité à 3 mois: 6%' };
      if (score < 30) return { level: 'danger', text: `MELD: ${score}`, detail: 'Mortalité à 3 mois: 20%' };
      return { level: 'danger', text: `MELD: ${score}`, detail: 'Mortalité à 3 mois: > 50%. Greffe urgente' };
    },
    formula: 'MELD = 10 × [0.957×ln(créat) + 0.378×ln(bili) + 1.12×ln(INR) + 0.643]'
  },

  blatchford: {
    name: 'Score de Blatchford',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Risque hémorragie digestive haute',
    color: 'amber',
    fields: [
      { id: 'urea', label: 'Urée (mmol/L)', type: 'select', options: [
        { value: 0, label: '< 6.5 (0 pts)' },
        { value: 2, label: '6.5-8.0 (2 pts)' },
        { value: 3, label: '8.0-10.0 (3 pts)' },
        { value: 4, label: '10.0-25.0 (4 pts)' },
        { value: 6, label: '≥ 25.0 (6 pts)' }
      ]},
      { id: 'hb', label: 'Hémoglobine', type: 'select', options: [
        { value: 0, label: 'H: ≥130 / F: ≥120 g/L (0 pts)' },
        { value: 1, label: 'H: 120-130 / F: 100-120 g/L (1 pt)' },
        { value: 3, label: 'H: 100-120 g/L (3 pts)' },
        { value: 6, label: '< 100 g/L (6 pts)' }
      ]},
      { id: 'sbp', label: 'PAS', type: 'select', options: [
        { value: 0, label: '≥ 110 mmHg (0 pts)' },
        { value: 1, label: '100-109 mmHg (1 pt)' },
        { value: 2, label: '90-99 mmHg (2 pts)' },
        { value: 3, label: '< 90 mmHg (3 pts)' }
      ]},
      { id: 'pulse', label: 'Pouls ≥ 100/min', type: 'checkbox', points: 1 },
      { id: 'melena', label: 'Méléna', type: 'checkbox', points: 1 },
      { id: 'syncope', label: 'Syncope', type: 'checkbox', points: 2 },
      { id: 'liver', label: 'Maladie hépatique', type: 'checkbox', points: 2 },
      { id: 'heart', label: 'Insuffisance cardiaque', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = (values.urea || 0) + (values.hb || 0) + (values.sbp || 0);
      if (values.pulse) total += 1;
      if (values.melena) total += 1;
      if (values.syncope) total += 2;
      if (values.liver) total += 2;
      if (values.heart) total += 2;
      return { score: total, max: 23 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'Blatchford 0', detail: 'Risque très faible. Retour à domicile possible' };
      if (score <= 2) return { level: 'success', text: `Blatchford ${score}`, detail: 'Risque faible. Hospitalisation courte' };
      if (score <= 6) return { level: 'warning', text: `Blatchford ${score}`, detail: 'Risque modéré. FOGD < 24h' };
      return { level: 'danger', text: `Blatchford ${score}`, detail: 'Risque élevé. FOGD urgente, réanimation si instable' };
    },
    formula: 'Blatchford = Urée + Hb + PAS + Pouls + Méléna + Syncope + Foie + Cœur'
  },

  ranson: {
    name: 'Score de Ranson',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Gravité pancréatite aiguë',
    color: 'amber',
    fields: [
      { id: 'age', label: 'Admission: Âge > 55 ans', type: 'checkbox', points: 1 },
      { id: 'wbc', label: 'Admission: GB > 16 000/mm³', type: 'checkbox', points: 1 },
      { id: 'glucose', label: 'Admission: Glycémie > 11 mmol/L (2 g/L)', type: 'checkbox', points: 1 },
      { id: 'ldh', label: 'Admission: LDH > 350 UI/L', type: 'checkbox', points: 1 },
      { id: 'ast', label: 'Admission: ASAT > 250 UI/L', type: 'checkbox', points: 1 },
      { id: 'hct', label: 'H48: Chute Hte > 10%', type: 'checkbox', points: 1 },
      { id: 'bun', label: 'H48: Augmentation urée > 1.8 mmol/L', type: 'checkbox', points: 1 },
      { id: 'calcium', label: 'H48: Calcémie < 2 mmol/L', type: 'checkbox', points: 1 },
      { id: 'pao2', label: 'H48: PaO₂ < 60 mmHg', type: 'checkbox', points: 1 },
      { id: 'baseDeficit', label: 'H48: Déficit base > 4 mEq/L', type: 'checkbox', points: 1 },
      { id: 'fluid', label: 'H48: Séquestration liquidienne > 6 L', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 11 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Ranson ${score}`, detail: 'Mortalité < 5%. Pancréatite légère' };
      if (score <= 4) return { level: 'warning', text: `Ranson ${score}`, detail: 'Mortalité 15-20%. Pancréatite modérée' };
      if (score <= 6) return { level: 'danger', text: `Ranson ${score}`, detail: 'Mortalité 40%. Pancréatite sévère' };
      return { level: 'danger', text: `Ranson ${score}`, detail: 'Mortalité > 99%. Pancréatite très sévère' };
    },
    formula: 'Ranson: 5 critères à l\'admission + 6 critères à 48h'
  },

  alvarado: {
    name: 'Score d\'Alvarado (MANTRELS)',
    category: 'Chirurgie',
    icon: Scissors,
    description: 'Probabilité appendicite aiguë',
    color: 'pink',
    fields: [
      { id: 'migration', label: 'Migration douleur FID', type: 'checkbox', points: 1 },
      { id: 'anorexia', label: 'Anorexie', type: 'checkbox', points: 1 },
      { id: 'nausea', label: 'Nausées/vomissements', type: 'checkbox', points: 1 },
      { id: 'tenderness', label: 'Douleur FID', type: 'checkbox', points: 2 },
      { id: 'rebound', label: 'Rebond (défense)', type: 'checkbox', points: 1 },
      { id: 'fever', label: 'Température > 37.3°C', type: 'checkbox', points: 1 },
      { id: 'leukocytosis', label: 'Leucocytose > 10 000/mm³', type: 'checkbox', points: 2 },
      { id: 'shift', label: 'Déviation gauche (PNN > 75%)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.migration) total += 1;
      if (values.anorexia) total += 1;
      if (values.nausea) total += 1;
      if (values.tenderness) total += 2;
      if (values.rebound) total += 1;
      if (values.fever) total += 1;
      if (values.leukocytosis) total += 2;
      if (values.shift) total += 1;
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `Alvarado ${score}`, detail: 'Appendicite peu probable. Surveillance' };
      if (score <= 6) return { level: 'warning', text: `Alvarado ${score}`, detail: 'Appendicite possible. Scanner recommandé' };
      if (score <= 8) return { level: 'danger', text: `Alvarado ${score}`, detail: 'Appendicite probable. Avis chirurgical' };
      return { level: 'danger', text: `Alvarado ${score}`, detail: 'Appendicite très probable. Chirurgie' };
    },
    formula: 'MANTRELS: Migration, Anorexie, Nausées, Tenderness(2), Rebound, Elevation temp, Leukocytosis(2), Shift'
  }
};

// ==================== INFECTIOLOGIE ====================
export const INFECTIOUS_SCORES = {
  sepsis3: {
    name: 'Critères SEPSIS-3',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Définition sepsis et choc septique',
    color: 'rose',
    fields: [
      { id: 'infection', label: 'Infection suspectée ou documentée', type: 'checkbox', points: 0 },
      { id: 'sofa2', label: 'Augmentation SOFA ≥ 2 points', type: 'checkbox', points: 0 },
      { id: 'vasopressors', label: 'Besoin vasopresseurs (PAM ≥ 65)', type: 'checkbox', points: 0 },
      { id: 'lactate', label: 'Lactate > 2 mmol/L malgré remplissage', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      let status = 0;
      if (values.infection && values.sofa2) status = 1; // Sepsis
      if (status === 1 && values.vasopressors && values.lactate) status = 2; // Choc septique
      return { score: status, max: 2 };
    },
    interpret: (score, result, values) => {
      if (!values?.infection) return { level: 'success', text: 'Pas de sepsis', detail: 'Infection non suspectée' };
      if (score === 0) return { level: 'success', text: 'Infection sans sepsis', detail: 'SOFA < 2. Surveillance, traitement infection' };
      if (score === 1) return { level: 'danger', text: 'SEPSIS', detail: 'Infection + SOFA ≥ 2. Antibiothérapie urgente, remplissage, surveillance lactate' };
      return { level: 'danger', text: 'CHOC SEPTIQUE', detail: 'Vasopresseurs + Lactate > 2 mmol/L. Réanimation, antibiothérapie large spectre' };
    },
    formula: 'Sepsis = Infection + SOFA ≥ 2 | Choc = Sepsis + Vasopresseurs + Lactate > 2'
  },

  sofaComplet: {
    name: 'SOFA complet',
    category: 'Réanimation',
    icon: Activity,
    description: 'Sequential Organ Failure Assessment',
    color: 'orange',
    fields: [
      { id: 'pao2fio2', label: 'PaO₂/FiO₂', type: 'select', options: [
        { value: 0, label: '≥ 400 (0 pt)' },
        { value: 1, label: '300-399 (1 pt)' },
        { value: 2, label: '200-299 (2 pts)' },
        { value: 3, label: '100-199 avec VM (3 pts)' },
        { value: 4, label: '< 100 avec VM (4 pts)' }
      ]},
      { id: 'platelets', label: 'Plaquettes (×10³/µL)', type: 'select', options: [
        { value: 0, label: '≥ 150 (0 pt)' },
        { value: 1, label: '100-149 (1 pt)' },
        { value: 2, label: '50-99 (2 pts)' },
        { value: 3, label: '20-49 (3 pts)' },
        { value: 4, label: '< 20 (4 pts)' }
      ]},
      { id: 'bilirubin', label: 'Bilirubine (µmol/L)', type: 'select', options: [
        { value: 0, label: '< 20 (0 pt)' },
        { value: 1, label: '20-32 (1 pt)' },
        { value: 2, label: '33-101 (2 pts)' },
        { value: 3, label: '102-204 (3 pts)' },
        { value: 4, label: '> 204 (4 pts)' }
      ]},
      { id: 'cardiovascular', label: 'Cardiovasculaire', type: 'select', options: [
        { value: 0, label: 'PAM ≥ 70 (0 pt)' },
        { value: 1, label: 'PAM < 70 (1 pt)' },
        { value: 2, label: 'Dopamine ≤ 5 ou Dobutamine (2 pts)' },
        { value: 3, label: 'Dopamine > 5 ou Adré/NA ≤ 0.1 (3 pts)' },
        { value: 4, label: 'Dopamine > 15 ou Adré/NA > 0.1 (4 pts)' }
      ]},
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 0, label: '15 (0 pt)' },
        { value: 1, label: '13-14 (1 pt)' },
        { value: 2, label: '10-12 (2 pts)' },
        { value: 3, label: '6-9 (3 pts)' },
        { value: 4, label: '< 6 (4 pts)' }
      ]},
      { id: 'creatinine', label: 'Créatinine (µmol/L) ou diurèse', type: 'select', options: [
        { value: 0, label: '< 110 (0 pt)' },
        { value: 1, label: '110-170 (1 pt)' },
        { value: 2, label: '171-299 (2 pts)' },
        { value: 3, label: '300-440 ou diurèse < 500 mL/j (3 pts)' },
        { value: 4, label: '> 440 ou diurèse < 200 mL/j (4 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 24 };
    },
    interpret: (score) => {
      if (score <= 6) return { level: 'success', text: `SOFA ${score}`, detail: 'Mortalité < 10%' };
      if (score <= 9) return { level: 'warning', text: `SOFA ${score}`, detail: 'Mortalité 15-20%' };
      if (score <= 12) return { level: 'danger', text: `SOFA ${score}`, detail: 'Mortalité 40-50%' };
      return { level: 'danger', text: `SOFA ${score}`, detail: 'Mortalité > 80%' };
    },
    formula: 'SOFA = Respiration + Coagulation + Foie + Cardiovasculaire + Neurologique + Rénal'
  },

  macIsaac: {
    name: 'Score de Mac Isaac (Centor modifié)',
    category: 'ORL',
    icon: Ear,
    description: 'Dépistage angine bactérienne',
    color: 'rose',
    fields: [
      { id: 'fever', label: 'Fièvre > 38°C', type: 'checkbox', points: 1 },
      { id: 'noCough', label: 'Absence de toux', type: 'checkbox', points: 1 },
      { id: 'adenopathy', label: 'Adénopathies cervicales douloureuses', type: 'checkbox', points: 1 },
      { id: 'exudate', label: 'Exsudat amygdalien', type: 'checkbox', points: 1 },
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 1, label: '3-14 ans (+1 pt)' },
        { value: 0, label: '15-44 ans (0 pt)' },
        { value: -1, label: '≥ 45 ans (-1 pt)' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.age || 0);
      if (values.fever) total += 1;
      if (values.noCough) total += 1;
      if (values.adenopathy) total += 1;
      if (values.exudate) total += 1;
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `Mac Isaac ${score}`, detail: 'SGA improbable (< 10%). Pas d\'antibiothérapie ni TDR' };
      if (score <= 2) return { level: 'warning', text: `Mac Isaac ${score}`, detail: 'SGA possible (15-30%). TDR recommandé' };
      if (score <= 3) return { level: 'warning', text: `Mac Isaac ${score}`, detail: 'SGA probable (30-50%). TDR recommandé' };
      return { level: 'danger', text: `Mac Isaac ${score}`, detail: 'SGA très probable (> 50%). TDR, antibiothérapie si positif' };
    },
    formula: 'Mac Isaac = Fièvre + Pas de toux + Adénopathies + Exsudat + Âge'
  }
};

// ==================== PÉDIATRIE ÉTENDUE ====================
export const PEDIATRIC_EXTENDED_SCORES = {
  pram: {
    name: 'Score PRAM',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Sévérité crise d\'asthme pédiatrique',
    color: 'violet',
    fields: [
      { id: 'spo2', label: 'SpO₂', type: 'select', options: [
        { value: 0, label: '≥ 95% (0 pt)' },
        { value: 1, label: '92-94% (1 pt)' },
        { value: 2, label: '< 92% (2 pts)' }
      ]},
      { id: 'retractions', label: 'Tirage sus-sternal', type: 'select', options: [
        { value: 0, label: 'Absent (0 pt)' },
        { value: 2, label: 'Présent (2 pts)' }
      ]},
      { id: 'scalene', label: 'Contraction scalènes', type: 'select', options: [
        { value: 0, label: 'Absente (0 pt)' },
        { value: 2, label: 'Présente (2 pts)' }
      ]},
      { id: 'airEntry', label: 'Entrée d\'air', type: 'select', options: [
        { value: 0, label: 'Normale (0 pt)' },
        { value: 1, label: 'Diminuée à la base (1 pt)' },
        { value: 2, label: 'Diminuée apex et base (2 pts)' },
        { value: 3, label: 'Minimale ou absente (3 pts)' }
      ]},
      { id: 'wheezing', label: 'Sibilants', type: 'select', options: [
        { value: 0, label: 'Absents (0 pt)' },
        { value: 1, label: 'Expiratoires seuls (1 pt)' },
        { value: 2, label: 'Inspiratoires et expiratoires (2 pts)' },
        { value: 3, label: 'Audibles sans stéthoscope ou thorax silencieux (3 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: 'Crise légère', detail: 'Salbutamol 2-4 bouffées, réévaluation' };
      if (score <= 7) return { level: 'warning', text: 'Crise modérée', detail: 'Salbutamol + corticoïdes PO, surveillance' };
      if (score <= 11) return { level: 'danger', text: 'Crise sévère', detail: 'Nébulisations continues, corticoïdes IV, Mg IV à discuter' };
      return { level: 'danger', text: 'Crise très sévère', detail: 'Réanimation, adrénaline, IOT à envisager' };
    },
    formula: 'PRAM = SpO₂ + Tirage + Scalènes + Entrée air + Sibilants'
  },

  wang: {
    name: 'Score de Wang',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Sévérité bronchiolite',
    color: 'violet',
    fields: [
      { id: 'resp', label: 'Fréquence respiratoire', type: 'select', options: [
        { value: 0, label: '< 30/min (0 pt)' },
        { value: 1, label: '31-45/min (1 pt)' },
        { value: 2, label: '46-60/min (2 pts)' },
        { value: 3, label: '> 60/min (3 pts)' }
      ]},
      { id: 'wheezing', label: 'Sibilants', type: 'select', options: [
        { value: 0, label: 'Absents (0 pt)' },
        { value: 1, label: 'Fin d\'expiration au stéthoscope (1 pt)' },
        { value: 2, label: 'Toute l\'expiration au stéthoscope (2 pts)' },
        { value: 3, label: 'Audibles à l\'oreille (3 pts)' }
      ]},
      { id: 'retractions', label: 'Tirage', type: 'select', options: [
        { value: 0, label: 'Absent (0 pt)' },
        { value: 1, label: 'Intercostal seul (1 pt)' },
        { value: 2, label: 'Sus-claviculaire (2 pts)' },
        { value: 3, label: 'Battement ailes du nez (3 pts)' }
      ]},
      { id: 'general', label: 'État général', type: 'select', options: [
        { value: 0, label: 'Normal (0 pt)' },
        { value: 1, label: 'Irritable, alimentation diminuée (1 pt)' },
        { value: 2, label: 'Alimentation très diminuée (2 pts)' },
        { value: 3, label: 'Apathique, refuse alimentation (3 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: 'Bronchiolite légère', detail: 'DRP, surveillance ambulatoire' };
      if (score <= 6) return { level: 'warning', text: 'Bronchiolite modérée', detail: 'Hospitalisation à discuter, O₂ si SpO₂ < 92%' };
      if (score <= 9) return { level: 'danger', text: 'Bronchiolite sévère', detail: 'Hospitalisation, O₂, surveillance continue' };
      return { level: 'danger', text: 'Bronchiolite très sévère', detail: 'USI, VNI/OHD, IOT si épuisement' };
    },
    formula: 'Wang = FR + Sibilants + Tirage + État général'
  },

  guarino: {
    name: 'Score de Guarino',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Déshydratation sur diarrhée aiguë',
    color: 'violet',
    fields: [
      { id: 'general', label: 'État général', type: 'select', options: [
        { value: 0, label: 'Normal, éveillé (0 pt)' },
        { value: 1, label: 'Agité, irritable (1 pt)' },
        { value: 2, label: 'Léthargique, somnolent (2 pts)' }
      ]},
      { id: 'eyes', label: 'Yeux', type: 'select', options: [
        { value: 0, label: 'Normaux (0 pt)' },
        { value: 1, label: 'Légèrement enfoncés (1 pt)' },
        { value: 2, label: 'Très enfoncés (2 pts)' }
      ]},
      { id: 'mucosa', label: 'Muqueuses', type: 'select', options: [
        { value: 0, label: 'Humides (0 pt)' },
        { value: 1, label: 'Collantes (1 pt)' },
        { value: 2, label: 'Sèches (2 pts)' }
      ]},
      { id: 'tears', label: 'Larmes', type: 'select', options: [
        { value: 0, label: 'Présentes (0 pt)' },
        { value: 1, label: 'Diminuées (1 pt)' },
        { value: 2, label: 'Absentes (2 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: 'Déshydratation absente/minime', detail: 'SRO ambulatoire' };
      if (score <= 4) return { level: 'warning', text: 'Déshydratation modérée (3-6%)', detail: 'SRO surveillance, hospitalisation si échec' };
      return { level: 'danger', text: 'Déshydratation sévère (≥6%)', detail: 'Hospitalisation, réhydratation IV urgente' };
    },
    formula: 'Guarino = État général + Yeux + Muqueuses + Larmes'
  },

  westley: {
    name: 'Score de Westley',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Gravité laryngite',
    color: 'violet',
    fields: [
      { id: 'stridor', label: 'Stridor', type: 'select', options: [
        { value: 0, label: 'Absent (0 pt)' },
        { value: 1, label: 'À l\'agitation (1 pt)' },
        { value: 2, label: 'Au repos (2 pts)' }
      ]},
      { id: 'retractions', label: 'Tirage', type: 'select', options: [
        { value: 0, label: 'Absent (0 pt)' },
        { value: 1, label: 'Léger (1 pt)' },
        { value: 2, label: 'Modéré (2 pts)' },
        { value: 3, label: 'Sévère (3 pts)' }
      ]},
      { id: 'airEntry', label: 'Entrée d\'air', type: 'select', options: [
        { value: 0, label: 'Normale (0 pt)' },
        { value: 1, label: 'Diminuée (1 pt)' },
        { value: 2, label: 'Très diminuée (2 pts)' }
      ]},
      { id: 'cyanosis', label: 'Cyanose', type: 'select', options: [
        { value: 0, label: 'Absente (0 pt)' },
        { value: 4, label: 'À l\'agitation (4 pts)' },
        { value: 5, label: 'Au repos (5 pts)' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: 'Normale (0 pt)' },
        { value: 5, label: 'Altérée (5 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 17 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: 'Laryngite légère', detail: 'Dexaméthasone PO, surveillance ambulatoire' };
      if (score <= 7) return { level: 'warning', text: 'Laryngite modérée', detail: 'Dexaméthasone + adrénaline nébulisée si besoin' };
      if (score <= 11) return { level: 'danger', text: 'Laryngite sévère', detail: 'Adrénaline nébulisée + corticoïdes, hospitalisation' };
      return { level: 'danger', text: 'Laryngite très sévère', detail: 'Réanimation, IOT à envisager' };
    },
    formula: 'Westley = Stridor + Tirage + Entrée air + Cyanose + Conscience'
  }
};

// ==================== PSYCHIATRIE ====================
export const PSYCHIATRY_SCORES = {
  hamilton: {
    name: 'Échelle Hamilton Dépression',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Évaluation dépression',
    color: 'indigo',
    fields: [
      { id: 'mood', label: 'Humeur dépressive', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Tristesse occasionnelle' },
        { value: 2, label: '2 - Tristesse permanente' }, { value: 3, label: '3 - Tristesse intense' },
        { value: 4, label: '4 - Désespoir extrême' }
      ]},
      { id: 'guilt', label: 'Sentiments de culpabilité', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Auto-accusation' },
        { value: 2, label: '2 - Culpabilité, ruminations' }, { value: 3, label: '3 - Idées de punition' },
        { value: 4, label: '4 - Hallucinations culpabilisantes' }
      ]},
      { id: 'suicide', label: 'Suicide', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Vie ne vaut pas la peine' },
        { value: 2, label: '2 - Souhaite être mort' }, { value: 3, label: '3 - Idées suicidaires' },
        { value: 4, label: '4 - Tentative de suicide' }
      ]},
      { id: 'insomnia', label: 'Insomnie', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée (réveil nocturne)' }
      ]},
      { id: 'work', label: 'Travail et activités', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Légèrement diminué' },
        { value: 2, label: '2 - Perte d\'intérêt' }, { value: 3, label: '3 - Diminution temps activité' },
        { value: 4, label: '4 - Arrêt travail' }
      ]},
      { id: 'psychomotor', label: 'Ralentissement', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Léger' },
        { value: 2, label: '2 - Évident' }, { value: 3, label: '3 - Entretien difficile' },
        { value: 4, label: '4 - Stupeur' }
      ]},
      { id: 'anxiety', label: 'Anxiété psychique', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sévère' },
        { value: 4, label: '4 - Incapacitante' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 28 }; // Version simplifiée 7 items
    },
    interpret: (score) => {
      if (score <= 7) return { level: 'success', text: 'Absence de dépression', detail: 'Pas de traitement spécifique' };
      if (score <= 13) return { level: 'warning', text: 'Dépression légère', detail: 'Psychothérapie, surveillance' };
      if (score <= 18) return { level: 'warning', text: 'Dépression modérée', detail: 'Antidépresseur + psychothérapie' };
      return { level: 'danger', text: 'Dépression sévère', detail: 'Traitement urgent, hospitalisation si risque suicidaire' };
    },
    formula: 'Hamilton: 17-21 items selon version (ici version courte 7 items)'
  },

  had: {
    name: 'Échelle HAD',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Hospital Anxiety and Depression Scale',
    color: 'indigo',
    fields: [
      { id: 'a1', label: 'Je me sens tendu(e) ou énervé(e)', type: 'select', options: [
        { value: 3, label: 'La plupart du temps' }, { value: 2, label: 'Souvent' },
        { value: 1, label: 'De temps en temps' }, { value: 0, label: 'Jamais' }
      ]},
      { id: 'd1', label: 'Je prends plaisir aux mêmes choses qu\'autrefois', type: 'select', options: [
        { value: 0, label: 'Oui, tout autant' }, { value: 1, label: 'Pas autant' },
        { value: 2, label: 'Un peu seulement' }, { value: 3, label: 'Presque plus' }
      ]},
      { id: 'a2', label: 'J\'ai une sensation de peur comme si quelque chose allait arriver', type: 'select', options: [
        { value: 3, label: 'Oui, très nettement' }, { value: 2, label: 'Oui, mais ce n\'est pas grave' },
        { value: 1, label: 'Un peu' }, { value: 0, label: 'Pas du tout' }
      ]},
      { id: 'd2', label: 'Je ris facilement et vois le bon côté des choses', type: 'select', options: [
        { value: 0, label: 'Autant qu\'avant' }, { value: 1, label: 'Plus autant' },
        { value: 2, label: 'Vraiment moins' }, { value: 3, label: 'Plus du tout' }
      ]},
      { id: 'a3', label: 'Je me fais du souci', type: 'select', options: [
        { value: 3, label: 'Très souvent' }, { value: 2, label: 'Assez souvent' },
        { value: 1, label: 'Occasionnellement' }, { value: 0, label: 'Très occasionnellement' }
      ]},
      { id: 'd3', label: 'Je suis de bonne humeur', type: 'select', options: [
        { value: 3, label: 'Jamais' }, { value: 2, label: 'Rarement' },
        { value: 1, label: 'Assez souvent' }, { value: 0, label: 'La plupart du temps' }
      ]},
      { id: 'a4', label: 'Je peux rester tranquillement assis(e)', type: 'select', options: [
        { value: 0, label: 'Oui, quoi qu\'il arrive' }, { value: 1, label: 'Oui, en général' },
        { value: 2, label: 'Rarement' }, { value: 3, label: 'Jamais' }
      ]}
    ],
    calculate: (values) => {
      const anxiety = (values.a1 || 0) + (values.a2 || 0) + (values.a3 || 0) + (values.a4 || 0);
      const depression = (values.d1 || 0) + (values.d2 || 0) + (values.d3 || 0);
      return { score: anxiety + depression, max: 21, details: { anxiety, depression } };
    },
    interpret: (score, result) => {
      const a = result?.details?.anxiety || 0;
      const d = result?.details?.depression || 0;
      let text = [];
      if (a <= 7) text.push('Anxiété normale');
      else if (a <= 10) text.push('Anxiété légère');
      else text.push('Anxiété sévère');
      
      if (d <= 7) text.push('Pas de dépression');
      else if (d <= 10) text.push('Dépression légère');
      else text.push('Dépression sévère');
      
      const level = (a > 10 || d > 10) ? 'danger' : (a > 7 || d > 7) ? 'warning' : 'success';
      return { level, text: text.join(' | '), detail: `Anxiété: ${a}/21 | Dépression: ${d}/21 (version courte)` };
    },
    formula: 'HAD: 14 items (7 anxiété + 7 dépression). Score > 10 = significatif'
  }
};

// Combine all scores
export const ALL_MEDICAL_SCORES = {
  ...ANESTHESIA_SCORES,
  ...BURNS_SCORES,
  ...CARDIOLOGY_EXTENDED_SCORES,
  ...NEPHROLOGY_SCORES,
  ...GASTRO_SCORES,
  ...INFECTIOUS_SCORES,
  ...PEDIATRIC_EXTENDED_SCORES,
  ...PSYCHIATRY_SCORES
};

// Categories for filtering
export const SCORE_CATEGORIES = {
  'Anesthésie': { icon: Syringe, color: 'emerald' },
  'Brûlés': { icon: Flame, color: 'orange' },
  'Cardiologie': { icon: Heart, color: 'red' },
  'Néphrologie': { icon: Droplets, color: 'blue' },
  'Hépato-Gastro': { icon: Beaker, color: 'amber' },
  'Chirurgie': { icon: Scissors, color: 'pink' },
  'Infectiologie': { icon: Thermometer, color: 'rose' },
  'ORL': { icon: Ear, color: 'rose' },
  'Réanimation': { icon: Activity, color: 'orange' },
  'Pédiatrie': { icon: Baby, color: 'violet' },
  'Psychiatrie': { icon: Brain, color: 'indigo' },
  'Neurologie': { icon: Brain, color: 'purple' },
  'Pneumologie': { icon: Wind, color: 'cyan' },
  'Urgences': { icon: Stethoscope, color: 'orange' }
};
